// plan-04 S03: Oracle-framed render — bible S20

import type { GeneratedPost } from './generate'
import type { CommandCtx } from '../../oracle/commands'
import { createStreamTarget } from '../../oracle/streaming'
import { play } from '../../audio/sfx'
import { load, bump, set } from '../../core/save'
import { exportCard, downloadBlob } from '../../core/exportCard'
import { renderDivineContent } from './exportContent'

export async function renderDivine(ctx: CommandCtx, post: GeneratedPost): Promise<void> {
  const { history, cursor, streamFn, abortSignal } = ctx

  // @network header
  const netLine = createStreamTarget(history, 'vt-line-oracle vt-line-dim')
  netLine.style.color = 'var(--vt-green-dim)'
  cursor.attach(netLine)
  await streamFn(netLine, '@network', { signal: abortSignal })
  cursor.detach()

  // > X Y - Z A.  (the bright line — term-green)
  const bodyLine = createStreamTarget(history, 'vt-line-oracle')
  bodyLine.style.color = 'var(--vt-term-green)'
  cursor.attach(bodyLine)
  await streamFn(bodyLine, `> ${post.sentence}`, { signal: abortSignal })
  cursor.detach()

  // > probablyTag
  const tagLine = createStreamTarget(history, 'vt-line-oracle')
  tagLine.style.color = 'var(--vt-term-green)'
  cursor.attach(tagLine)
  await streamFn(tagLine, `> ${post.probablyTag}`, { signal: abortSignal })
  cursor.detach()

  // blank separator
  const blankLine = createStreamTarget(history, 'vt-line-oracle')
  blankLine.textContent = ''

  // metadata block — dim
  // @quorum
  const quorumLine = createStreamTarget(history, 'vt-line-oracle')
  quorumLine.style.color = 'var(--vt-green-dim)'
  cursor.attach(quorumLine)
  await streamFn(quorumLine, `@quorum: ${post.quorumTag}.`, { signal: abortSignal })
  cursor.detach()

  // @certainty
  const certLine = createStreamTarget(history, 'vt-line-oracle')
  certLine.style.color = 'var(--vt-green-dim)'
  const certText = document.createTextNode('@certainty: ')
  certLine.appendChild(certText)
  const certLink = document.createElement('a')
  certLink.href = post.gematriaUrl
  certLink.target = '_blank'
  certLink.rel = 'noopener'
  certLink.style.color = 'var(--vt-green-dim)'
  certLink.style.textDecoration = 'underline'
  certLink.textContent = `${post.certainty} remaining`
  certLine.appendChild(certLink)
  certLine.appendChild(document.createTextNode('.'))
  cursor.attach(certLine)
  await streamFn(certLine, '', { signal: abortSignal, sfx: false })
  cursor.detach()

  // @verdict
  const verdictLine = createStreamTarget(history, 'vt-line-oracle')
  verdictLine.style.color = 'var(--vt-green-dim)'
  cursor.attach(verdictLine)
  await streamFn(verdictLine, `@verdict: ${post.verdictText}.`, { signal: abortSignal })
  cursor.detach()

  // @hash with copy link button
  const hashLine = createStreamTarget(history, 'vt-line-oracle')
  hashLine.style.color = 'var(--vt-green-dim)'
  const hashText = document.createTextNode(`@hash: ${post.hash}  `)
  hashLine.appendChild(hashText)

  const copyBtn = document.createElement('button')
  copyBtn.textContent = '[ copy link ]'
  copyBtn.style.color = 'var(--vt-green-dim)'
  copyBtn.style.background = 'none'
  copyBtn.style.border = 'none'
  copyBtn.style.cursor = 'pointer'
  copyBtn.style.fontFamily = 'var(--vt-font-mono)'
  copyBtn.style.fontSize = 'inherit'
  copyBtn.style.padding = '0'
  copyBtn.style.margin = '0'
  copyBtn.style.textDecoration = 'underline'
  copyBtn.setAttribute('role', 'button')

  const shareUrl = `https://vaguepostking.com/#divine=${post.hash}`
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      play('command_accept')
      hashLine.style.color = 'var(--vt-amber)'
      setTimeout(() => {
        hashLine.style.color = 'var(--vt-green-dim)'
      }, 1600)
    } catch {
      // clipboard write failed — silent
    }
  })

  hashLine.appendChild(copyBtn)

  // plan-19 S06: [ export ] button next to [ copy link ]
  const exportBtn = document.createElement('button')
  exportBtn.textContent = '  [ export ]'
  exportBtn.style.color = 'var(--vt-green-dim)'
  exportBtn.style.background = 'none'
  exportBtn.style.border = 'none'
  exportBtn.style.cursor = 'pointer'
  exportBtn.style.fontFamily = 'var(--vt-font-mono)'
  exportBtn.style.fontSize = 'inherit'
  exportBtn.style.padding = '0'
  exportBtn.style.margin = '0'
  exportBtn.style.textDecoration = 'underline'
  exportBtn.setAttribute('role', 'button')
  exportBtn.addEventListener('click', async () => {
    play('command_accept')
    const originalLabel = exportBtn.textContent
    exportBtn.textContent = '  [ rendering... ]'
    exportBtn.style.pointerEvents = 'none'
    try {
      const filename = `vpk-divine-${post.hash}.png`
      const blob = await exportCard({
        title: 'VAGUEPOST.GEN // DIVINATION',
        headerLine: `> divine. hash: ${post.hash}`,
        content: { kind: 'text', render: renderDivineContent(post) },
        stampUrl: `vaguepostking.com/#divine=${post.hash}`,
        stampHash: `@${post.hash.slice(0, 4)}`,
        filename,
      })
      downloadBlob(blob, filename)
      play('quorum_chime')
      const s = load()
      set('cardExports', { ...s.cardExports, divine: s.cardExports.divine + 1 })
      hashLine.style.color = 'var(--vt-amber)'
      setTimeout(() => { hashLine.style.color = 'var(--vt-green-dim)' }, 1600)
    } catch {
      play('command_reject')
      const err = createStreamTarget(history, 'vt-line-oracle')
      err.style.color = 'var(--vt-green-dim)'
      err.textContent = '> export failed. consensus: probably.'
    } finally {
      exportBtn.textContent = originalLabel
      exportBtn.style.pointerEvents = ''
    }
  })
  hashLine.appendChild(exportBtn)

  cursor.attach(hashLine)
  await streamFn(hashLine, '', { signal: abortSignal, sfx: false })
  cursor.detach()

  // repeat detection
  const save = load()
  if (save.divineSeen.includes(post.hash)) {
    const repeatLine = createStreamTarget(history, 'vt-line-oracle')
    repeatLine.style.color = 'var(--vt-green-dim)'
    repeatLine.style.fontStyle = 'italic'
    cursor.attach(repeatLine)
    await streamFn(repeatLine, '// you have observed this consensus before. probably.', { signal: abortSignal })
    cursor.detach()
  }

  // track in divineSeen
  const seen = save.divineSeen.filter((h) => h !== post.hash)
  seen.push(post.hash)
  set('divineSeen', seen)
  bump('events')

  // quorum_chime on render complete
  play('quorum_chime')
}
